import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BillingSubscriptionRequest, SubscriptionInfo } from '@b3networks/api/subscription';
import { X_B3_HEADER } from '@b3networks/shared/common';
import { HashMap } from '@datorama/akita';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { BuyerWallet } from '../buyer-wallet/buyer-wallet.model';
import { DomainLiability } from '../domain-liability';
import { TempCreditLimit } from '../temp-credit-limit';
import { TransferCreditReq } from '../transfer-credit-req';
import { BalanceForBuyer } from './buyer-wallet';
import { BalanceType, BillingEntity, Wallet, WalletEntity } from './wallet';
import { WalletStore } from './wallet.store';

@Injectable({
  providedIn: 'root'
})
export class WalletService {
  constructor(
    private http: HttpClient,
    private store: WalletStore
  ) {}

  getWallet(): Observable<Wallet> {
    return this.http.get<Wallet>(`/billing/private/v4/wallet`).pipe(
      map(res => new Wallet(res)),
      tap(wallet => this.store.update({ wallet: wallet }))
    );
  }

  transferCredit(toWalletUuid: string, transferCreditRequest: TransferCreditReq) {
    return this.http.post(`/billing/private/v2/wallet/credit/transfer/${toWalletUuid}`, transferCreditRequest);
  }

  getTempCreditLimits(): Observable<TempCreditLimit[]> {
    return this.http.get<TempCreditLimit[]>(`/billing/private/v2/temp-credit-limits`);
  }

  getDomainLiable(): Observable<DomainLiability> {
    return this.http.get<DomainLiability>(`/billing/private/admin/liability`);
  }

  getBuyerTag(buyerUuid: string): Observable<HashMap<string>> {
    return this.http.get<HashMap<string>>(`/billing/private/v6/buyerTag/${buyerUuid}`);
  }

  getBuyerWallet(buyerOrgUuid: string): Observable<BuyerWallet[]> {
    return this.http
      .get<BuyerWallet[]>(`/billing/private/v6/buyerWallets/${buyerOrgUuid}`)
      .pipe(map(res => res.map(item => new BuyerWallet(item))));
  }

  getSellerWallet(sellerOrgUuid: string): Observable<BuyerWallet[]> {
    return this.http
      .get<BuyerWallet[]>(`/billing/private/v6/sellerWallets/${sellerOrgUuid}`)
      .pipe(map(res => res.map(item => new BuyerWallet(item))));
  }

  putBuyerTag(buyerUuid: string, req: HashMap<string>): Observable<void> {
    return this.http.put<void>(`/billing/private/v6/buyerTag/${buyerUuid}`, req);
  }

  getBalanceMovementForBuyer(buyerUuid: string, currency: string): Observable<BalanceForBuyer[]> {
    return this.http
      .get<BalanceForBuyer[]>(`/data/private/balanceMovementForBuyer/${buyerUuid}/${currency}`)
      .pipe(map(res => res.map(item => new BalanceForBuyer(item))));
  }

  getBalanceMovementForSeller(sellerUuid: string, currency: string): Observable<BalanceForBuyer[]> {
    return this.http
      .get<BalanceForBuyer[]>(`/data/private/balanceMovementForSeller/${sellerUuid}/${currency}`)
      .pipe(map(res => res.map(item => new BalanceForBuyer(item))));
  }

  updateBuyerWallets(buyerUuid: string, currency: string, postpaid: boolean) {
    const url = `/billing/private/v6/buyerWallets/${buyerUuid}/${currency}`;
    return this.http.put<void>(url, { postpaid: !postpaid });
  }

  initWallet(orgUuid: string, currency: string): Observable<void> {
    return this.http.post<void>(`/billing/private/v6/wallet`, { buyerOrgUuid: orgUuid, currency });
  }

  getWalletEntity(orgUuid?: string): Observable<WalletEntity> {
    let headers = new HttpHeaders();
    if (orgUuid) {
      headers = headers.set(X_B3_HEADER.orgUuid, orgUuid);
    }
    return this.http.get<WalletEntity>(`/billing/private/v1/entity`, { headers }).pipe(
      tap(walletEntity => {
        this.store.update({ walletEntity: walletEntity });
      })
    );
  }

  getEntityByBilling(orgUuid?: string): Observable<BillingEntity> {
    return this.http
      .get<BillingEntity>(`/billing/private/v1/entityIdOf/${orgUuid}`)
      .pipe(tap(entityId => this.store.update({ entityId: entityId })));
  }

  getWalletByOrgUuid(orgUuid: string): Observable<Wallet> {
    return this.http.get<Wallet>(`/billing/private/v4/admin/wallet/${orgUuid}`).pipe(map(res => this.toWallet(res)));
  }

  billingSubscriptionInfo(body: BillingSubscriptionRequest): Observable<SubscriptionInfo[]> {
    return this.http
      .post<SubscriptionInfo[]>(`/billing/private/v8/subscriptionInfo`, body)
      .pipe(map(items => items.map(item => new SubscriptionInfo(item))));
  }

  billingAddOnInfo(body): Observable<SubscriptionInfo[]> {
    return this.http.post<SubscriptionInfo[]>(`/billing/private/v8/addOn/addInfo`, body);
  }

  changeSubscriptionPlanInfo(body: BillingSubscriptionRequest): Observable<SubscriptionInfo> {
    return this.http.post<SubscriptionInfo>(`/billing/private/v8/changeSubscriptionPlanInfo`, body);
  }

  private toWallet(res): Wallet {
    const wallet: Wallet = Object.assign(new Wallet(), res);
    wallet.balance = Object.assign(new BalanceType(), wallet.balance);
    return wallet;
  }
}
